import React, { useState } from "react";
import {
  ColumnDef,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { PaymentOptions } from "@/types";
import { Transaction, SellableItem, TransactionTable } from "@/types/pos/transaction";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import {
  capitalizeFirstLetter,
  displayDate,
  displayValue,
  formatNIC,
  formatToPKR,
  replaceUnderscoreWithSpace,
  saleHistoryMapper,
  hasFullAccessToMemberNIC,
  downloadCSV,
  downloadPDF,
} from "@/lib/helper";
import Pagination from "@/components/ui/table/pagination-table";
import { DataTableViewOptions } from "./data-table-view-options";
import { Separator } from "@/components/ui/separator";
import { DataTableColumnHeader } from "@/components/ui/table/data-table-column-header";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { SaleStatus } from "@/types/enums";
import { useLazyGetSalesReportForExportQuery, useGetSalesReportQuery } from "@/services/transactionApi";
import SalesSummary from "../summary/sales-summary";

interface SalesReportTableProps {
  appliedFilters: Record<string, any>;
}

export default function SalesReportTable({
  appliedFilters
}: SalesReportTableProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  const pos_sale_report = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .pos_sale_report ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const hide_pos_erp = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .hide_pos_erp ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  // Local state for table-specific functionality
  const [inputValue, setInputValue] = useState("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "updated_at", desc: true },
  ]);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Build query for table with server-side pagination
  const buildTableQuery = () => {
    const params = new URLSearchParams();
    params.append("limit", String(pageSize));
    params.append("offset", String(currentPage * pageSize));
    params.append("sort_order", sorting[0]?.desc ? "desc" : "asc");
    params.append("load_summary", "true");
    params.append("sort_key", sorting[0]?.id || "updated_at");

    // Add search term if present
    if (searchTerm) {
      params.append("search_key", searchTerm);
    }

    // Add applied filters to query
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        // Handle arrays (multiselect) - append each value separately
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              params.append(key, String(val));
            }
          });
        } else {
          params.append(key, String(value));
        }
      }
    });

    return params.toString();
  };

  // API call for table data
  const { data: tableApiData, isLoading } = useGetSalesReportQuery(
    { query: buildTableQuery(), org_id: orgId },
    { skip: false }
  );

  const salesReportTableData = React.useMemo(() => {
    return Array.isArray(tableApiData?.data) ? tableApiData?.data : [];
  }, [tableApiData]);

  // Reset to first page when filters or search changes
  React.useEffect(() => {
    setCurrentPage(0);
  }, [appliedFilters, searchTerm, sorting]);

  // Mock toggleSortOrder for column headers (we can implement actual sorting if needed)
  const toggleSortOrder = (key: string, order: string) => {
    // For now, just update local sorting state
    if (!order) {
      setSorting([{ id: "updated_at", desc: true }]);
    } else {
      setSorting([{ id: key, desc: order === "desc" }]);
    }
  };

  // Mock searchCriteria for column headers
  const searchCriteria = {
    sort_key: sorting[0]?.id || "updated_at",
    sort_order: sorting[0]?.desc ? 'desc' : 'asc'
  };

  const { toast } = useToast();

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [showSelectAllNotification, setShowSelectAllNotification] = useState(false);
  const [getSalesReportForExport] = useLazyGetSalesReportForExportQuery();

  // Helper function to build export query (all data without pagination)
  const buildExportQuery = (includeSummary = false) => {
    const exportQuery = new URLSearchParams();
    exportQuery.append("offset", "0");
    exportQuery.append("sort_order", sorting[0]?.desc ? "desc" : "asc");
    if (includeSummary) {
      exportQuery.append("load_summary", "true");
    }
    exportQuery.append("sort_key", sorting[0]?.id || "updated_at");

    // Add search term if present
    if (searchTerm) {
      exportQuery.append("search_key", searchTerm);
    }

    // Add applied filters to query
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          value.forEach((val) => {
            if (val) {
              exportQuery.append(key, String(val));
            }
          });
        } else {
          exportQuery.append(key, String(value));
        }
      }
    });

    return exportQuery.toString();
  };

  const handleExportCSV = async () => {
    if (selectAllRows) {
      // Export all filtered data - fetch all records without pagination
      try {
        const result = await getSalesReportForExport({ query: buildExportQuery(), org_id: orgId }).unwrap();

        if (result?.data && result.data.length > 0) {
          downloadCSV(result.data as any[], "all_sales_report.csv", saleHistoryMapper);
          toast({
            variant: "success",
            title: `Exported ${result.data.length} transactions successfully`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
            description: "No transaction records found to export.",
          });
        }
      } catch (error) {
        console.error("Export failed:", error);
        toast({
          variant: "destructive",
          title: "Export failed",
          description: "Failed to export data. Please try again.",
        });
      }
    } else {
      // Export selected rows
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }
      downloadCSV(selectedRows as any[], "sales_report.csv", saleHistoryMapper);
    }
  };

  const handleExportPDF = async (orientation: 'portrait' | 'landscape' = 'portrait') => {
    if (selectAllRows) {
      // Export all filtered data as PDF - fetch all records without pagination
      try {
        const result = await getSalesReportForExport({ query: buildExportQuery(true), org_id: orgId }).unwrap();

        if (result?.data && result.data.length > 0) {
          await downloadPDF(
            {
              data: result.data,
              total_counts: result.total_counts || result.data.length,
              filtered_counts: result.filtered_counts || result.data.length,
              summary: result.summary || tableApiData?.summary
            } as unknown as TransactionTable,
            `all_sales_report.pdf`,
            "Sales Report - All Transactions",
            {
              start_date: appliedFilters.start_date,
              end_date: appliedFilters.end_date
            },
            orientation
          );
          toast({
            variant: "success",
            title: `PDF exported successfully`,
            description: `${result.data.length} transactions exported to PDF`,
          });
        } else {
          toast({
            variant: "destructive",
            title: "No data to export",
            description: "No transaction records found to export.",
          });
        }
      } catch (error) {
        console.error("PDF Export failed:", error);
        toast({
          variant: "destructive",
          title: "PDF Export failed",
          description: "Failed to export PDF. Please try again.",
        });
      }
    } else {
      // Export selected rows as PDF
      const selectedRows = table
        .getSelectedRowModel()
        .rows.map((row) => row.original);
      if (selectedRows.length === 0) {
        toast({
          variant: "destructive",
          title: "Please select one or more records to perform this action.",
        });
        return;
      }

      try {
        await downloadPDF(
          { data: selectedRows, total_counts: tableApiData?.total_counts, filtered_counts: selectedRows.length, summary: tableApiData?.summary } as unknown as TransactionTable,
          `selected_sales_report.pdf`,
          "Sales Report - Selected Transactions",
          {
            start_date: appliedFilters.start_date,
            end_date: appliedFilters.end_date
          },
          orientation
        );
        toast({
          variant: "success",
          title: `PDF exported successfully`,
          description: `${selectedRows.length} selected transactions exported to PDF`,
        });
      } catch (error) {
        console.error("PDF Export failed:", error);
        toast({
          variant: "destructive",
          title: "PDF Export failed",
          description: "Failed to export PDF. Please try again.",
        });
      }
    }
  };

  const erpStatusColumn: ColumnDef<Transaction> = {
    accessorKey: "erp_status",
    meta: "ERP Status",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="ERP Status"
        sortKey="erp_status"
        searchCriteria={searchCriteria}
        toggleSortOrder={toggleSortOrder}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex gap-2 items-center justify-between w-fit">
          <div className="">
            <p className="capitalize cursor-pointer">
              <span>{displayValue(row.original.erp_status)}</span>
            </p>
          </div>
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
  };

  const cnicNtnColumn: ColumnDef<Transaction> = {
    accessorKey: "nic",
    meta: "Nic",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="CNIC/NTN"
        sortKey="id"
        sortable={false}
        searchCriteria={searchCriteria}
        toggleSortOrder={toggleSortOrder}
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="text-nowrap whitespace-nowrap ">
          {displayValue(row?.original.is_business ? row?.original.member_nic : formatNIC(row?.original.member_nic))}
        </div>
      );
    },
  };

  // Data is already paginated from the server, no need for client-side pagination

  const columns: ColumnDef<Transaction>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => {
        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value: any) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="translate-y-[2px]"
          />
        );
      },
    },
    {
      accessorKey: "receipt_number",
      meta: "Receipt Number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Txn Number"
          sortKey="id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            {row.getCanExpand() && (
              <button
                {...{
                  onClick: row.getToggleExpandedHandler(),
                }}
                className="flex gap-1 items-center"
              >
                {row.getIsExpanded() ? (
                  <i className="fa fa-angle-down w-3 h-3"></i>
                ) : (
                  <i className="fa fa-angle-right w-3 h-3"></i>
                )}
              </button>
            )}

            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{displayValue(row.original.receipt_number)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "srb_invoice_id",
      meta: "SRB Number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="SRB Number"
          sortKey="srb_invoice_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.srb_invoice_id)}
          </div>
        );
      },
    },
    {
      accessorKey: "invoice_number",
      meta: "Invoice Number",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Invoice Number"
          sortKey="invoice_number"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.invoice_number)}
          </div>
        );
      },
    },
    {
      accessorKey: "member_name",
      meta: "Member Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Member Name"
          sortKey="member_name"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),

      cell: ({ row }) => {
        return (
          <div className="flex capitalize items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.member_name)}
          </div>
        );
      },
    },
    ...(hasFullAccessToMemberNIC() ? [cnicNtnColumn] : []),
    {
      accessorKey: "income_category_id",
      meta: "Income Category",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Income Category"
          sortKey="income_category_id"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2 text-ellipsis whitespace-nowrap overflow-hidden">

            {(() => {
              const uniqueIncomeCategories = Array.from(
                new Set(row.original.items.map((item: SellableItem) => item.income_category_name).filter(Boolean))
              );
              const displayedCategories = uniqueIncomeCategories.slice(0, 2);
              const remainingCount = uniqueIncomeCategories.length - displayedCategories.length;

              return (
                <>
                  {displayedCategories.map((categoryName, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {displayValue(categoryName)}
                    </span>
                  ))}
                  {remainingCount > 0 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      +{remainingCount} more
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        );
      },
    },
    {
      accessorKey: "payment_method",
      meta: "Payment Method",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Payment Method"
          sortKey="payment_method"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2 text-ellipsis whitespace-nowrap overflow-hidden">

            {(() => {
              return (
                <>
                  {row.original.payments.slice(0, 2).map((item, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {displayValue(item.payment_method)}
                    </span>
                  ))}
                  {row.original.payments.length - 2 > 0 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      +{row.original.payments.length - 2} more
                    </span>
                  )}
                </>
              );
            })()}
          </div>
        );
      },
    },
    {
      accessorKey: "counter_name",
      meta: "Counter Name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Counter Name"
          sortKey="counter_name"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="text-nowrap whitespace-nowrap ">
            {displayValue(row?.original.counter_name)}
          </div>
        );
      },
    },
    {
      accessorKey: "net_price",
      meta: "Net Price",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Net Price"
          sortKey="net_price"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="text-nowrap whitespace-nowrap ">
            {displayValue(formatToPKR(row?.original.sub_total ?? row?.original.net_price))}
          </div>
        );
      },
    },
    {
      accessorKey: "discount",
      meta: "Discounted Amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Discount Amount"
          sortKey="discount"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{formatToPKR(row.original.discount)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "subtotal_after_discount",
      meta: "Subtotal",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Subtotal"
          sortKey="discount"
          sortable={false}
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{formatToPKR(Math.max(0, Number(row.original.net_price ?? row.original.sub_total ?? 0) - Number(row.original.discount ?? 0)))}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "tax_amount",
      meta: "Tax amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Tax Amount"
          sortKey="tax_amount"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            {formatToPKR(row?.original.tax_amount)}
          </div>
        );
      },
    },
    {
      accessorKey: "total",
      meta: "total amount",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Total Amount"
          sortKey="total"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>
                  {formatToPKR(
                    (row.original.transaction_type == SaleStatus.Refund
                      ? -Number(row.original.total)
                      : row.original.total) as number
                  )}
                </span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "status",
      meta: "Status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer">
                <span>{displayValue(row.original.status)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "staff_name",
      meta: "CreatedBy",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created By"
          sortKey="created_by"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="capitalize cursor-pointer">
                      <span>
                        {(() => {
                          const name = row.original.staff_name;
                          if (!name || name === "null" || name === "undefined") return "App User";
                          return name.length > 15 ? name.substring(0, 15) + "..." : name;
                        })()}
                      </span>
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="capitalize text-sm">
                      {displayValue(`${row?.original?.staff_name}`)}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "created_at",
      meta: "date",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created Date"
          sortKey="created_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer text-nowrap">
                <span>{displayDate(row?.original.created_at)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "updated_at",
      meta: "Updated At",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Updated At"
          sortKey="updated_at"
          searchCriteria={searchCriteria}
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex gap-2 items-center justify-between w-fit">
            <div className="">
              <p className="capitalize cursor-pointer text-nowrap">
                <span>{displayDate(row?.original.updated_at)}</span>
              </p>
            </div>
          </div>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
  ];

  const table = useReactTable({
    data: salesReportTableData as Transaction[],
    columns,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowCanExpand: () => true,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
  });

  // Handle showing select all notification
  React.useEffect(() => {
    const allPageRowsSelected = table.getIsAllPageRowsSelected();
    const selectedRowCount = table.getSelectedRowModel().rows.length;
    const totalRowsInCurrentPage = table.getRowModel().rows.length;

    if (allPageRowsSelected && selectedRowCount === totalRowsInCurrentPage && totalRowsInCurrentPage > 0 && !selectAllRows) {
      setShowSelectAllNotification(true);
    } else if (selectedRowCount === 0 || selectAllRows) {
      setShowSelectAllNotification(false);
    }
  }, [table.getIsAllPageRowsSelected(), table.getSelectedRowModel().rows.length, selectAllRows]);

  // Reset select all when row selection changes
  React.useEffect(() => {
    if (Object.keys(rowSelection).length === 0) {
      setSelectAllRows(false);
      setShowSelectAllNotification(false);
    }
  }, [rowSelection]);

  // Auto-select all visible rows when selectAllRows is true and data changes
  React.useEffect(() => {
    if (selectAllRows && salesReportTableData.length > 0) {
      table.toggleAllPageRowsSelected(true);
    }
  }, [selectAllRows, salesReportTableData, table]);

  const totalRecords = tableApiData?.filtered_counts || 0;

  const handleSelectAllRows = () => {
    setSelectAllRows(true);
    setShowSelectAllNotification(false);
  };

  const handleClearSelection = () => {
    setSelectAllRows(false);
    setShowSelectAllNotification(false);
    table.toggleAllPageRowsSelected(false);
    setRowSelection({});
  };

  // Pagination handlers
  const handleLimitChange = (newLimit: number) => {
    setPageSize(newLimit);
    setCurrentPage(0);
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(totalRecords / pageSize) - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(0);
  };

  const handleLastPage = () => {
    setCurrentPage(Math.ceil(totalRecords / pageSize) - 1);
  };

  const isLastPage = currentPage >= Math.ceil(totalRecords / pageSize) - 1;

  return (
    <div className="w-full space-y-4">
      {/* Summary Cards at the top
    
      */}
      {tableApiData?.summary && (
        <SalesSummary data={tableApiData} isLoading={isLoading} />
      )}

      <div className="bg-white rounded-md p-3 py-4 space-y-3">

        {/* Search + Export — single row always */}
        <div className="flex flex-row gap-2 w-full items-center">
          <div className="flex-1 min-w-0">
            <TableSearchFilter
              placeHolder="Search by Txn Number"
              setSearchCriteria={() => { }}
              inputValue={inputValue}
              setInputValue={(value: React.SetStateAction<string>) => {
                if (typeof value === 'string') {
                  setInputValue(value);
                  setSearchTerm(value);
                }
              }}
            />
          </div>
          <div className="flex-shrink-0">
            <DataTableViewOptions
              table={table}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
            />
          </div>
        </div>

        {/* Select All Notification */}
        {(showSelectAllNotification || selectAllRows) && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-2 sm:p-3 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
            <div className="text-xs sm:text-sm text-blue-800">
              {selectAllRows ? (
                <>
                  All <strong>{totalRecords}</strong> transactions are selected.
                </>
              ) : (
                <>
                  All <strong>{table.getSelectedRowModel().rows.length}</strong> transactions on this page are selected.
                </>
              )}
            </div>
            {!selectAllRows && (
              <button
                onClick={handleSelectAllRows}
                className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium"
              >
                Select all {totalRecords} transactions
              </button>
            )}
            {selectAllRows && (
              <button
                onClick={handleClearSelection}
                className="text-blue-600 hover:text-blue-800 underline text-xs sm:text-sm font-medium"
              >
                Clear selection
              </button>
            )}
          </div>
        )}

        <div className="rounded-none border border-border  ">
          <ScrollArea className="w-full relative" hideScrollToTop={true}>
            <ScrollBar
              orientation="horizontal"
              className="relative z-30 cursor-grab h-2"
            />
            <Table className="w-full sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
              <TableHeader className="bg-secondary">
                {table?.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      <div className="flex space-x-2 justify-center items-center bg-white ">
                        <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                        <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                        <div className="size-3 bg-black rounded-full animate-bounce"></div>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows.length ? (
                  table.getRowModel().rows.map((row) => (
                    <>
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id} className="h-10">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                      {row.getIsExpanded() && (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="pr-4 sm:pr-40">
                            <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">
                              {row.original.transaction_type} Details
                            </h3>

                            {/* Desktop items grid */}
                            <div className="hidden md:block">
                              <div className="grid grid-cols-12 py-2 gap-4 font-semibold border-b text-sm">
                                <span className="col-span-1 text-left">Name</span>
                                <span className="col-span-2 text-left">Type</span>
                                <span className="col-span-1 text-left">Qty</span>
                                <span className="col-span-1 text-left">Unit Price</span>
                                <span className="col-span-1 text-left">Discount</span>
                                <span className="col-span-2 text-left">Tax Amount</span>
                                <span className="col-span-2 text-left">Sub Total</span>
                                <span className="col-span-1 text-right">Total</span>
                              </div>
                              <div className="grid grid-cols-12 py-2 gap-4 border-b last:border-b-0 text-sm">
                                {row.original.items?.map((item, i) => (
                                  <React.Fragment key={i}>
                                    <span className="capitalize col-span-1 text-left">{item.description}</span>
                                    <div className="w-full col-span-2 text-left text-nowrap">
                                      {capitalizeFirstLetter(replaceUnderscoreWithSpace(item.item_type))}
                                    </div>
                                    <div className="w-full col-span-1 text-left">{item.quantity || 0}</div>
                                    <div className="w-full col-span-1 text-left">{formatToPKR(Number(item.price?.toFixed(2)))}</div>
                                    <div className="w-full col-span-1 text-left">{formatToPKR(Number(item.discount.toFixed(2)))}</div>
                                    <div className="w-full col-span-2 text-left">{formatToPKR(Number(item.tax_amount?.toFixed(2)))}</div>
                                    <div className="w-full col-span-2 text-left text-nowrap">{formatToPKR(Number(((item.quantity || 0) * (item.price || 0)).toFixed(2)))}</div>
                                    <div className="w-full col-span-2 text-right">{formatToPKR(Number(item.total?.toFixed(2)))} {item.tax_type}</div>
                                  </React.Fragment>
                                ))}
                              </div>
                            </div>

                            {/* Mobile items cards */}
                            <div className="md:hidden space-y-2 mb-3">
                              {row.original.items?.map((item, i) => (
                                <div key={i} className="border rounded-lg p-3 bg-gray-50 text-sm space-y-1">
                                  <div className="font-semibold capitalize border-b pb-1">{item.description}</div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    <div><span className="text-gray-500">Type:</span> {capitalizeFirstLetter(replaceUnderscoreWithSpace(item.item_type))}</div>
                                    <div><span className="text-gray-500">Qty:</span> {item.quantity || 0}</div>
                                    <div><span className="text-gray-500">Unit Price:</span> {formatToPKR(Number(item.price?.toFixed(2)))}</div>
                                    <div><span className="text-gray-500">Discount:</span> {formatToPKR(Number(item.discount.toFixed(2)))}</div>
                                    <div><span className="text-gray-500">Tax:</span> {formatToPKR(Number(item.tax_amount?.toFixed(2)))}</div>
                                    <div className="font-semibold"><span className="text-gray-500">Total:</span> {formatToPKR(Number(item.total?.toFixed(2)))}</div>
                                  </div>
                                </div>
                              ))}
                            </div>

                            <Separator />
                            <div className="grid grid-cols-1 sm:grid-cols-5 grid-flow-col gap-4 mt-3">
                              <div className="sm:col-span-2">
                                Notes :
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <p>
                                        <span className="capitalize cursor-pointer">
                                          {displayValue(
                                            `${row.original.notes}`.length > 15
                                              ? `${row.original.notes}`.substring(0, 15) + "..."
                                              : `${row.original.notes}`
                                          )}
                                        </span>
                                      </p>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="capitalize text-sm max-w-80">
                                      <p>{displayValue(`${row?.original?.notes}`)}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <Separator className="w-[50%]" />
                                {row.original.status == "Paid" && <h4 className="text-base sm:text-lg font-semibold mb-2">Payment Details</h4>}
                                {row.original.status == "Paid" && (
                                  <div className="grid grid-cols-12 py-2 gap-4 font-semibold text-sm">
                                    <span className="col-span-4 text-nowrap">Payment Method</span>
                                    <span className="col-span-1">Amount</span>
                                  </div>
                                )}
                                {row.original.payments?.map((payment: PaymentOptions, i: number) => (
                                  <div key={i} className="grid grid-cols-12 py-2 gap-4 border-b last:border-b-0 text-sm">
                                    <span className="col-span-4">{displayValue(payment.payment_method)}</span>
                                    <span className="text-right">{formatToPKR(payment.amount)}</span>
                                  </div>
                                ))}
                              </div>
                              <div className="sm:col-span-3">
                                <div className="py-4 text-sm space-y-1">
                                  <div className="flex justify-between"><span>Net Price</span><span>{formatToPKR(row.original.net_price ?? row.original.sub_total)}</span></div>
                                  <div className="flex justify-between"><span>Discount</span><span>{formatToPKR(row.original.discount)}</span></div>
                                  <div className="flex justify-between"><span>Total Tax</span><span>{formatToPKR(row.original.tax_amount)}</span></div>
                                  <div className="flex justify-between"><span>Total</span><span>{formatToPKR(Number(row.original.total))}</span></div>
                                  {/* <div className="flex justify-between"><span>Change Given</span><span>{formatToPKR(Number(row.original.change_amount))}</span></div> */}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))
                ) : salesReportTableData.length > 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No sales report found.
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>

        {/* pagination */}
        {
          salesReportTableData.length > 0 && (
            <Pagination
              limit={pageSize}
              offset={currentPage * pageSize}
              totalItems={totalRecords}
              onLimitChange={handleLimitChange}
              onNextPage={handleNextPage}
              onPrevPage={handlePrevPage}
              onFirstPage={handleFirstPage}
              onLastPage={handleLastPage}
              isLastPage={isLastPage}
            />
          )
        }
      </div>
    </div >
  );
}
